import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { CountryMapper } from "@cuauth/country-mapper";

import { LANG_STORAGE_KEY } from "@/utils/locale/initial-locale";

import { getMessages } from "./getMessages";
import { I18nContext, type I18nContextValue } from "./I18nContext";

interface I18nProviderProps {
  /** Locale resolved from ui_locales (+ optional localStorage.lang). */
  locale: string;
  children: ReactNode;
}

/**
 * Provides locale-aware product copy to login/signup screens.
 *
 * `changeLocale` persists `localStorage.lang` and refreshes UI messages for
 * the current session. The language switcher UI is currently Switzerland-only;
 * see `shouldShowLanguageSwitcher`.
 */
export function I18nProvider({
  locale: initialLocale,
  children,
}: I18nProviderProps) {
  const [locale, setLocale] = useState(initialLocale);

  useEffect(() => {
    setLocale(initialLocale);
  }, [initialLocale]);

  // Locales supported for the current country (switcher options + changeLocale guard).
  const availableLocales = useMemo(() => {
    const countryCode = CountryMapper.parseCountryCodeWhenSupported(locale);
    return countryCode
      ? CountryMapper.getAvailableLocales(countryCode)
      : [locale];
  }, [locale]);

  const changeLocale = useCallback(
    (nextLocale: string) => {
      // Reject unsupported locales or ones outside the current country.
      const normalised = CountryMapper.normaliseLocale(nextLocale);
      if (!normalised || !availableLocales.includes(normalised)) {
        return;
      }

      try {
        localStorage.setItem(LANG_STORAGE_KEY, normalised);
      } catch (error) {
        // Persistence is best-effort; UI still updates for this session.
        console.warn("failed to persist locale in localStorage.lang", error);
      }

      setLocale(normalised);
    },
    [availableLocales],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      messages: getMessages(locale),
      changeLocale,
      showLanguageSwitcher: shouldShowLanguageSwitcher(locale),
      availableLocales,
    }),
    [availableLocales, changeLocale, locale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Language switcher is currently Switzerland-only; other multi-locale
 * countries do not show it even when `availableLocales` has more than one entry.
 */
function shouldShowLanguageSwitcher(
  locale: string | null | undefined,
): boolean {
  return CountryMapper.parseCountryCodeWhenSupported(locale ?? null) === "CH";
}
